<template>
  <div class="ai-chat-page">
    <Breadcrumbs main="AI Чат" title="Бизнес-промпт"/>

    <AccessDenied v-if="forbidden"/>

    <div v-else class="bp-shell">
      <aside class="bp-sidebar">
        <BusinessPromptHistory
            :versions="versions"
            :selected-id="selectedId"
            :loading="loadingHistory"
            @select="onSelectVersion"
        />
      </aside>
      <main class="bp-main">
        <BusinessPromptEditor
            :current="selectedVersion"
            :can-edit="canEdit && isCurrent"
            :saving="saving"
            :server-errors="serverErrors"
            @save="onSave"
        />
      </main>
    </div>
  </div>
</template>

<script>
import api from '@/api/api';
import { canUseAiChat, canManageAiChat, permissionsKnown } from '@/common/permissions';
import BusinessPromptEditor from '@/components/AiChat/BusinessPromptEditor.vue';
import BusinessPromptHistory from '@/components/AiChat/BusinessPromptHistory.vue';
import AccessDenied from '@/components/AiChat/AccessDenied.vue';

export default {
  name: 'BusinessPromptPage',
  components: { BusinessPromptEditor, BusinessPromptHistory, AccessDenied },
  data() {
    return {
      current: null,
      versions: [],
      selectedId: null,
      loadingHistory: false,
      saving: false,
      editForbidden: false,
      serverErrors: [],
    };
  },
  computed: {
    forbidden() {
      return permissionsKnown() && !canUseAiChat();
    },
    canEdit() {
      if (this.editForbidden) return false;
      if (permissionsKnown()) return canManageAiChat();
      return true;
    },
    selectedVersion() {
      if (this.selectedId === (this.current && this.current.id)) return this.current;
      return this.versions.find(v => v.id === this.selectedId) || this.current;
    },
    isCurrent() {
      if (!this.current) return true;
      return this.selectedId === this.current.id;
    },
  },
  created() {
    this.loadAll();
  },
  methods: {
    async loadAll() {
      await Promise.all([this.loadCurrent(), this.loadHistory()]);
      if (this.current) this.selectedId = this.current.id;
    },

    async loadCurrent() {
      try {
        const resp = await api.aiChat.getBusinessPrompt();
        const data = (resp && resp.data && resp.data.data) || resp.data || null;
        this.current = data && data.id ? data : null;
      } catch (e) {
        void e;
      }
    },

    async loadHistory() {
      this.loadingHistory = true;
      try {
        const resp = await api.aiChat.getBusinessPromptHistory({ per_page: 50 });
        this.versions = (resp && resp.data && resp.data.data) || [];
      } catch (e) {
        void e;
      } finally {
        this.loadingHistory = false;
      }
    },

    onSelectVersion(v) {
      this.selectedId = v.id;
    },

    async onSave(text) {
      this.saving = true;
      this.serverErrors = [];
      try {
        const resp = await api.aiChat.updateBusinessPrompt({ text });
        const created = (resp && resp.data && resp.data.data) || resp.data;
        if (created) {
          this.current = created;
          this.versions = [created, ...this.versions];
          this.selectedId = created.id;
        }
        this.$toasted.show('Промпт сохранён', { type: 'success', duration: 3000 });
      } catch (e) {
        const status = e && e.response && e.response.status;
        if (status === 403) {
          this.editForbidden = true;
          this.$toasted.error('Нет прав на редактирование промпта (нужно ai-chat.manage)', { duration: 4000 });
        } else if (status === 422) {
          const errs = e.response.data && e.response.data.errors;
          if (errs) {
            const list = [];
            Object.keys(errs).forEach(k => {
              const v = errs[k];
              if (Array.isArray(v)) list.push(...v);
              else list.push(String(v));
            });
            this.serverErrors = list;
          } else if (e.response.data && e.response.data.message) {
            this.serverErrors = [e.response.data.message];
          }
        }
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.bp-shell {
  display: grid;
  grid-template-columns: 260px 1fr;
  height: calc(100vh - 220px);
  min-height: 520px;
  background: var(--ai-bg);
  border: 1px solid var(--ai-border);
  border-radius: 12px;
  overflow: hidden;
  margin: 0 1rem 1rem;
}
.bp-sidebar {
  background: var(--ai-sidebar);
  border-right: 1px solid var(--ai-border);
  min-width: 0;
  min-height: 0;
  height: 100%;
}
.bp-main {
  background: var(--ai-bg);
  min-width: 0;
  min-height: 0;
  height: 100%;
}

@media (max-width: 768px) {
  .bp-shell {
    grid-template-columns: 1fr;
    height: auto;
  }
  .bp-sidebar {
    height: 240px;
    border-right: none;
    border-bottom: 1px solid var(--ai-border);
  }
}
</style>
